import { apiClient } from '@/lib/api/client'

export type DashboardSignedProjectRowV2Dto = {
  projectId: string
  projectReference: string | null
  projectName: string | null
  projectTypeLabel: string | null
  quoteSignedAt: string | null
  invoicingPercentage: number | null
  invoicingStatus: string | null
  salesOwnerName: string | null
  orderId: string
  totalSaleAmount: number | null
  salesOrderUpdatedDate: string | null
  desiredPlacementDate: string | null
  placementDateStatus: string | null
  /** SOLY-REGIE-006 — badge the row: a 172 € retouche and a 25 000 € cuisine read alike otherwise. */
  isIntervention: boolean
}

export type DashboardSignedClientRowV2Dto = {
  clientId: string
  clientName: string | null
  projects: DashboardSignedProjectRowV2Dto[] | null
}

export type DashboardSignedProjectsPageV2Dto = {
  items: DashboardSignedClientRowV2Dto[] | null
  totalCount: number
  page: number
  pageSize: number
}

export type DashboardSalesOwnerV2Dto = {
  id: string
  fullName: string | null
  initials: string | null
}

export type DashboardCommercialDiscussionClientMilestoneV2Dto = {
  label: string | null
  status: string | null
}

export type DashboardCommercialDiscussionClientV2Dto = {
  customerId: string
  projectId: string | null
  name: string | null
  phone: string | null
  email: string | null
  totalProjectsCount: number | null
  salesOwner: DashboardSalesOwnerV2Dto | null
  milestones: DashboardCommercialDiscussionClientMilestoneV2Dto[] | null
  preferredLanguageLabel: string | null
  // SOLY-SALES-001 — follow-up substrat (mini-scope 02).
  // Note text is fetched on demand via the /follow-ups history endpoint.
  issuedAt: string | null
  lastFollowUpAt: string | null
  followUpCount: number
  // One of "fresh" | "warming" | "critical" | "archived" — null for non-issued states.
  urgencyBucket: string | null
  // SOLY-SALES-001 mini-scope 04 — populated when urgencyBucket === "archived".
  // "declined" | "superseded" | "inactive" | null.
  archiveReason: string | null
}

export type DashboardCommercialDiscussionCountsV2Dto = {
  all: number
  noQuote: number
  quoteDraft: number
  quoteIssued: number
}

export type DashboardCommercialDiscussionClientsV2Dto = {
  items: DashboardCommercialDiscussionClientV2Dto[] | null
  counts: DashboardCommercialDiscussionCountsV2Dto
}

export type DashboardGreetingV2Dto = {
  greeting: string
  dateLabel: string
}

export type DashboardHomeV2Dto = {
  greeting: DashboardGreetingV2Dto
  commercialDiscussionClients: DashboardCommercialDiscussionClientV2Dto[]
  signedProjects: DashboardSignedProjectsPageV2Dto
}

export type DashboardHomeHeaderV2Dto = {
  greeting: DashboardGreetingV2Dto
  commercialDiscussionClients: DashboardCommercialDiscussionClientV2Dto[]
}

export type GetDashboardSignedProjectsV2Params = {
  page: number
  pageSize: number
  orderBy?: 'quoteSignedAt' | 'salesOwnerName' | 'invoicingPercentage'
  orderDir?: 'asc' | 'desc'
}

export async function getDashboardCommercialDiscussionClientsV2(params?: {
  state?: 'no_quote' | 'quote_draft' | 'quote_issued'
}): Promise<DashboardCommercialDiscussionClientsV2Dto> {
  const query = new URLSearchParams()
  if (params?.state) {
    query.set('state', params.state)
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiClient.get<DashboardCommercialDiscussionClientsV2Dto>(
    `/api/v2/sales/dashboard/commercial-discussion/clients${suffix}`,
  )
}

export async function getDashboardHomeHeaderV2(): Promise<DashboardHomeHeaderV2Dto> {
  const now = new Date()

  return {
    greeting: {
      greeting: 'Bonjour',
      dateLabel: now.toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' }),
    },
    commercialDiscussionClients: [],
  }
}

export async function getDashboardSignedProjectsV2(
  params: GetDashboardSignedProjectsV2Params,
): Promise<DashboardSignedProjectsPageV2Dto> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  })

  if (params.orderBy) {
    query.set('orderBy', params.orderBy)
  }
  if (params.orderDir) {
    query.set('orderDir', params.orderDir)
  }

  return apiClient.get<DashboardSignedProjectsPageV2Dto>(`/api/v2/sales/dashboard/signed-projects?${query.toString()}`)
}

export async function getDashboardHomeV2(params: GetDashboardSignedProjectsV2Params): Promise<DashboardHomeV2Dto> {
  const [header, signedProjects] = await Promise.all([
    getDashboardHomeHeaderV2(),
    getDashboardSignedProjectsV2(params),
  ])

  return { ...header, signedProjects }
}

// ── SOLY-REGIE-006 — « Interventions » ────────────────────────────────────────

/**
 * What the file is waiting for.
 *
 * NOT the render order — `STATE_ORDER` is, and it puts the relance first. The server will send them in
 * that same order once the sibling backend change lands; until then its cap keeps the head of a
 * draft-first list, so the screen is right either way (the front re-sorts) and it is the truncated
 * TAIL that is wrong. This union is a set of values, and the order it happens to be written in
 * means nothing.
 */
export type DashboardInterventionState = 'quote_draft' | 'quote_issued' | 'to_plan'

export type DashboardInterventionRowV2Dto = {
  projectId: string
  projectReference: string
  projectName: string | null
  clientId: string
  clientName: string
  state: DashboardInterventionState
  createdAt: string | null
  quoteIssuedAt: string | null
  salesOwnerName: string | null
}

export type DashboardInterventionsV2Dto = {
  items: DashboardInterventionRowV2Dto[] | null
  /**
   * True when the server cap cut the list. Optional because the backend ships first by rule: on a
   * frontend that landed early it is simply absent, which reads as « not truncated » - the safe
   * direction, since the list is then short anyway.
   */
  hasMore?: boolean
}

export async function getDashboardInterventionsV2(): Promise<DashboardInterventionsV2Dto> {
  return apiClient.get<DashboardInterventionsV2Dto>('/api/v2/sales/dashboard/interventions')
}
